// Pay adjustments (Phase 3) — recurring/one-time allowances, per-diems and ad-hoc
// deductions on top of base salary. HR manages per employee; flows into the run.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Checkbox } from '@mui/material';
import { Plus, Trash2, Pencil, Coins } from 'lucide-react';
import { Card, Pill, PrimaryBtn, OutlineBtn } from './ui';
import { AG } from './theme/tokens';
import { api } from './api';

const KINDS = ['Earning', 'Deduction'];
const CATEGORIES = ['Allowance', 'PerDiem', 'Incentive', 'Reimbursement', 'Other'];
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function AdjustmentsCard({ employeeId, showToast }) {
  const editable = !!employeeId; // HR manages with an employeeId; employees view their own read-only
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => (employeeId ? api.payAdjustments(employeeId) : api.myAdjustments()).then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId]);

  const blank = { code: 'ALLOWANCE', label: '', kind: 'Earning', category: 'Allowance', amount: '', taxable: true, recurring: true, month: thisMonth() };
  const save = async () => {
    if (!form.label.trim() || !form.amount || busy) return; setBusy(true);
    const payload = { ...form, amount: Number(form.amount) };
    try {
      if (form.id) await api.updatePayAdjustment(form.id, payload); else await api.addPayAdjustment(employeeId, payload);
      showToast?.('Adjustment saved'); setForm(null); load();
    } catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const remove = async (a) => { try { await api.deletePayAdjustment(a.id); load(); } catch (e) { showToast?.(e.message); } };

  if (!rows) return null;
  const money = (n) => `Rs ${Number(n || 0).toLocaleString('en-IN')}`;
  return (
    <Card style={{ padding: 18 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
        <div className="flex items-center gap-2"><Coins size={16} style={{ color: AG.primary }} /><span style={{ fontWeight: 600, color: AG.text }}>Allowances & adjustments</span></div>
        {editable && <OutlineBtn onClick={() => setForm({ ...blank })}><Plus size={13} style={{ marginRight: 4 }} /> Add</OutlineBtn>}
      </div>
      {rows.length === 0 ? <p style={{ fontSize: 12.5, color: AG.n500 }}>No allowances or adjustments. Base salary only.</p> : (
        <div style={{ display: 'grid', gap: 6 }}>
          {rows.map((a) => (
            <div key={a.id} className="flex items-center justify-between" style={{ padding: '8px 10px', border: `1px solid ${AG.divider}`, borderRadius: 8, gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: AG.text }}>
                  {a.label} <Pill tone={a.kind === 'Earning' ? 'success' : 'warning'}>{a.kind === 'Earning' ? '+' : '−'} {money(a.amount)}</Pill>
                  {a.kind === 'Earning' && !a.taxable && <Pill tone="neutral">tax-free</Pill>}
                </div>
                <div style={{ fontSize: 11.5, color: AG.n500, marginTop: 1 }}>{a.category}{a.recurring ? ' · monthly' : ` · one-time ${a.month}`}{a.active ? '' : ' · inactive'}</div>
              </div>
              {editable && (
                <div className="flex items-center gap-1">
                  <button onClick={() => setForm({ ...a, amount: String(a.amount), month: a.month || thisMonth() })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 3 }}><Pencil size={14} /></button>
                  <button onClick={() => remove(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 3 }}><Trash2 size={14} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{form?.id ? 'Edit adjustment' : 'Add adjustment'}</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6, display: 'grid', gap: 12 }}>
              <TextField size="small" label="Label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Transport allowance" />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Type</label>
                  <select className="ag-input" style={{ marginTop: 4 }} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>{KINDS.map((k) => <option key={k}>{k}</option>)}</select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Category</label>
                  <select className="ag-input" style={{ marginTop: 4 }} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
                </div>
              </div>
              <TextField size="small" type="number" label="Amount (monthly)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} inputProps={{ min: 0 }} />
              {form.kind === 'Earning' && <FormControlLabel control={<Checkbox checked={!!form.taxable} onChange={(e) => setForm({ ...form, taxable: e.target.checked })} />} label="Taxable (adds to income-tax base)" />}
              <FormControlLabel control={<Checkbox checked={!!form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} />} label="Recurring (every payroll run)" />
              {!form.recurring && <TextField size="small" type="month" label="Month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} InputLabelProps={{ shrink: true }} />}
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={save} disabled={busy || !form?.label?.trim() || !form?.amount}>Save</PrimaryBtn></DialogActions>
      </Dialog>
    </Card>
  );
}
